import { useState, useEffect } from 'react'

interface SalesSummary {
  totalOrders: number
  totalQty: number
  totalBuyerPayment: number
  totalOmset: number
  totalDiscount: number
  totalCommission: number
  totalServiceFee: number
}

export default function Sales() {
  const [salesData, setSalesData] = useState<SalesSummary | null>(null)
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSalesData = async (date: string) => {
    try {
      setLoading(true)
      const response = await fetch(`/api/sales?date=${date}`)
      if (!response.ok) throw new Error('Failed to fetch sales data')
      
      const data = await response.json()
      setSalesData(data)
      setError(null)
    } catch (err) {
      console.error('Sales fetch error:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch sales data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSalesData(selectedDate)
  }, [selectedDate])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const calculateNetProfit = () => {
    if (!salesData) return 0
    return salesData.totalOmset - salesData.totalCommission - salesData.totalServiceFee
  }

  const calculateProfitMargin = () => {
    if (!salesData || salesData.totalOmset === 0) return 0
    return ((calculateNetProfit() / salesData.totalOmset) * 100)
  }

  if (loading && !salesData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Loading Sales Data...</h1>
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1>📊 Sales Analytics</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <button 
            onClick={() => fetchSalesData(selectedDate)}
            style={{ padding: '0.5rem 1rem', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{ 
          padding: '1rem', 
          background: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '8px', 
          marginBottom: '2rem',
          border: '1px solid #f5c6cb'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {salesData && (
        <>
          {/* Main Metrics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div style={{ padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#007bff' }}>📦 Total Orders</h3>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#007bff' }}>
                {salesData.totalOrders}
              </p>
              <small style={{ color: '#6c757d' }}>Total Quantity: {salesData.totalQty}</small>
            </div>

            <div style={{ padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#28a745' }}>💰 Total Omset</h3>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: '#28a745' }}>
                {formatCurrency(salesData.totalOmset)}
              </p>
              <small style={{ color: '#6c757d' }}>Gross Revenue</small>
            </div>

            <div style={{ padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#17a2b8' }}>💳 Buyer Payment</h3>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: '#17a2b8' }}>
                {formatCurrency(salesData.totalBuyerPayment)}
              </p>
              <small style={{ color: '#6c757d' }}>Total Received</small>
            </div>

            <div style={{ padding: '1.5rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#fd7e14' }}>📈 Net Profit</h3>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: '#fd7e14' }}>
                {formatCurrency(calculateNetProfit())}
              </p>
              <small style={{ color: '#6c757d' }}>
                Margin: {calculateProfitMargin().toFixed(1)}%
              </small>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1.5rem' }}>💼 Financial Breakdown</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              {/* Revenue Section */}
              <div>
                <h4 style={{ color: '#28a745', marginBottom: '1rem' }}>📊 Revenue</h4>
                <div style={{ space: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                    <span>Total Omset:</span>
                    <strong style={{ color: '#28a745' }}>{formatCurrency(salesData.totalOmset)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                    <span>Buyer Payment:</span>
                    <strong style={{ color: '#17a2b8' }}>{formatCurrency(salesData.totalBuyerPayment)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                    <span>Total Discount:</span>
                    <strong style={{ color: '#dc3545' }}>-{formatCurrency(salesData.totalDiscount)}</strong>
                  </div>
                </div>
              </div>

              {/* Costs Section */}
              <div>
                <h4 style={{ color: '#dc3545', marginBottom: '1rem' }}>💸 Costs & Fees</h4>
                <div style={{ space: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                    <span>Commission Fee:</span>
                    <strong style={{ color: '#dc3545' }}>-{formatCurrency(salesData.totalCommission)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                    <span>Service Fee:</span>
                    <strong style={{ color: '#dc3545' }}>-{formatCurrency(salesData.totalServiceFee)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '2px solid #333', marginTop: '1rem' }}>
                    <span><strong>Net Profit:</strong></span>
                    <strong style={{ color: calculateNetProfit() >= 0 ? '#28a745' : '#dc3545' }}>
                      {formatCurrency(calculateNetProfit())}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginBottom: '1.5rem' }}>📈 Performance Metrics</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div style={{ textAlign: 'center', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#007bff' }}>Average Order Value</h4>
                <p style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                  {salesData.totalOrders > 0 ? formatCurrency(salesData.totalOmset / salesData.totalOrders) : formatCurrency(0)}
                </p>
              </div>
              
              <div style={{ textAlign: 'center', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#28a745' }}>Profit Margin</h4>
                <p style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                  {calculateProfitMargin().toFixed(1)}%
                </p>
              </div>
              
              <div style={{ textAlign: 'center', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#ffc107' }}>Items per Order</h4>
                <p style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                  {salesData.totalOrders > 0 ? (salesData.totalQty / salesData.totalOrders).toFixed(1) : '0'}
                </p>
              </div>
              
              <div style={{ textAlign: 'center', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#dc3545' }}>Fee Rate</h4>
                <p style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                  {salesData.totalOmset > 0 ? (((salesData.totalCommission + salesData.totalServiceFee) / salesData.totalOmset) * 100).toFixed(1) : '0'}%
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {!salesData && !loading && (
        <div style={{ 
          padding: '3rem', 
          textAlign: 'center', 
          background: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
        }}>
          <h3>No sales data available for {selectedDate}</h3>
          <p style={{ color: '#6c757d' }}>Try selecting a different date or check if sales data has been imported.</p>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', marginTop: '2rem', color: '#6c757d' }}>
          Loading sales data...
        </div>
      )}
    </div>
  )
}
